import { LogOut, Edit3, ChevronRight, User, HelpCircle as HelpIcon, Info as InfoIcon, Camera, ArrowLeft } from "lucide-react";
import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { AuthContext } from '../../context/AuthContext';
import Cropper from "react-easy-crop";
import API_BASE_URL from "../../config";
import api from "../../utils/api";
import { createPortal } from 'react-dom';
import HelpScreen from '../dashboard/HelpScreen';

const Loader = () => createPortal(
  <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-lime-400"></div>
    <p className="text-white ml-4">Loading...</p>
  </div>,
  document.body
);


const ProfileSettings = () => {
  const [user, setUser] = useState({
    name: "",
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    //birthDate: "",
    gender: "",
    photoUrl: "",
    totalVisits: "",
    planExpiresInDays: "",
    planExpirydate: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [aboutMode, setAboutMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [helpMode, setHelpMode] = useState(false);

  const editRef = useRef(null);
  const aboutRef = useRef(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImagePreview, setCroppedImagePreview] = useState(null);
  const [banner, setBanner] = useState({ message: '', type: '', show: false });






  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImage = useCallback(async () => {
    try {
      if (!croppedAreaPixels || !imageSrc) {
        return;
      }

      const image = await createImage(imageSrc);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Use croppedAreaPixels directly (already in pixel coordinates)
      const cropX = croppedAreaPixels.x;
      const cropY = croppedAreaPixels.y;
      const cropWidth = croppedAreaPixels.width;
      const cropHeight = croppedAreaPixels.height;

      // Fixed 200x200 output for profile
      canvas.width = 200;
      canvas.height = 200;

      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        200,
        200
      );

      const croppedImageUrl = canvas.toDataURL("image/jpeg", 0.8);
      setCroppedImagePreview(croppedImageUrl);

      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const fileName = "profile.jpg";
      const file = new File([blob], fileName, { type: "image/jpeg" });

      setUser(prev => ({ ...prev, photoFile: file }));
      setCropOpen(false);
      setImageSrc(null);
    } catch (e) {
      console.error("Error cropping image", e);
    }
  }, [croppedAreaPixels, imageSrc]);


  const { logout } = useContext(AuthContext);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
      if (croppedImagePreview) URL.revokeObjectURL(croppedImagePreview);
    };
  }, [imageSrc, croppedImagePreview]);


  const fetchUserProfile = async () => {
    try {
      const userinfo = JSON.parse(localStorage.getItem("fitscan_user"));
      const accessToken = JSON.parse(localStorage.getItem("accessToken") || '{}').token || '';
      if (!userinfo || !accessToken) return;

      const response = await api.post('/api/profile/userprofile', { username: userinfo.username });
      const data = response.data;
      if (response.status !== 200 || !data) return;

      setUser((prev) => ({
        ...prev,
        name: data.fullName || "",
        firstname: data.firstName || "",
        lastname: data.lastName || "",
        email: data.email || "",
        phone: data.phoneNumber || "",
        photoUrl: data.photoUrl || "",
        //birthDate: data.birthDate || "",
        gender: data.gender || "",
        totalVisits: data.totalvisits || "",
        planExpiresInDays: data.expiresIn || "",
        planExpirydate: data.userExpiryDateShort || "",
      }));
    } catch (err) {
      console.error("Error fetching user profile", err);
    }
  };

  const handleEditClick = () => {
    const next = !editMode;
    setEditMode(next);
    if (next) {
      setTimeout(() => {
        editRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleAboutClick = () => {
    const next = !aboutMode;
    setAboutMode(next);
    if (next) {
      setTimeout(() => {
        aboutRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSaveProfile = async () => {
    setBanner({ message: '', type: '', show: false });
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('firstname', user.firstname);
      formData.append('lastname', user.lastname);
      formData.append('gender', user.gender);
      formData.append('phone', user.phone);
      formData.append('username', user.email); // Ensure you send a unique identifier

      if (user.photoFile) {
        formData.append('photo', user.photoFile);
      }

      const res = await fetch(`${API_BASE_URL}/api/Profile/updateProfile`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${JSON.parse(localStorage.getItem("accessToken") || '{}').token || ''}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setIsLoading(false);
        // SUCCESS BANNER
        setBanner({
          message: 'Profile updated successfully!',
          type: 'success',
          show: true
        });

        // REFRESH PROFILE & CLEANUP
        setUser(prev => ({
          ...prev,
          photoUrl: data.photoUrl,
          photoFile: null,      // Clear photoFile
          firstname: data.firstName || prev.firstname,
          lastname: data.lastName || prev.lastname,
          gender: data.gender || prev.gender,
          phone: data.phoneNumber || prev.phone,
          email: data.email || prev.email
        }));

        // Hide preview, show filename only
        setCroppedImagePreview(null);

        // Auto-hide banner after 3s
        setTimeout(() => {
          setBanner({ message: '', type: '', show: false });
        }, 3000);
      } else {
        setIsLoading(false);
        const error = await res.text();
        setBanner({
          message: `Failed to update profile: ${error}`,
          type: 'error',
          show: true
        });
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error updating profile:', error);
      setBanner({
        message: 'Unexpected error. Please try again later.',
        type: 'error',
        show: true
      });
    }
  };






  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (

    <>
      {isLoading && <Loader />}
      {helpMode ? (
        <HelpScreen onBack={() => setHelpMode(false)} />
      ) : editMode ? (
        <div className="max-w-xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-8">
            <button onClick={() => setEditMode(false)} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-2xl font-bold text-white">Edit Profile</h1>
          </div>
          <div ref={editRef} className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-6 space-y-6 text-white shadow-xl">
            {/* Banner Logic... */}
            {banner.show && (
              <div className={`fixed top-10 left-1/2 transform -translate-x-1/2 z-[1000] w-11/12 max-w-sm p-4 rounded-xl shadow-2xl border ${banner.type === 'success' ? 'bg-green-500/20 border-green-500 text-green-100' : 'bg-red-500/20 border-red-500 text-red-100'
                } backdrop-blur-md`}>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-sm">{banner.message}</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider font-bold mb-2 ml-1">First Name</label>
              <input
                type="text"
                value={user.firstname}
                onChange={(e) => setUser({ ...user, firstname: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#0d0d0d] border border-[#2b2b2b] text-white focus:border-[#6CD800] transition-colors outline-none"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider font-bold mb-2 ml-1">Last Name</label>
              <input
                type="text"
                value={user.lastname}
                onChange={(e) => setUser({ ...user, lastname: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#0d0d0d] border border-[#2b2b2b] text-white focus:border-[#6CD800] transition-colors outline-none"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider font-bold mb-2 ml-1">Gender</label>
              <select
                value={user.gender}
                onChange={(e) => setUser({ ...user, gender: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#0d0d0d] border border-[#2b2b2b] text-white focus:border-[#6CD800] transition-colors outline-none appearance-none"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider font-bold mb-2 ml-1">Profile Photo</label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#6CD800] flex items-center justify-center overflow-hidden text-2xl font-bold text-[#181f10] relative shrink-0">
                  {croppedImagePreview ? (
                    <img src={croppedImagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : user.photoUrl ? (
                    <img src={user.photoUrl.replace(/\\/g, '/')} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    user.firstname?.charAt(0) || '?'
                  )}
                </div>
                <button
                  onClick={() => document.getElementById('file-upload').click()}
                  className="flex-1 bg-[#222] border border-[#2b2b2b] hover:border-[#6CD800] text-gray-300 py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <Camera size={18} />
                  <span>Change Photo</span>
                </button>
                <input type="file" id="file-upload" className="hidden" accept="image/*" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImageSrc(URL.createObjectURL(file));
                    setCropOpen(true);
                  }
                }} />
              </div>
            </div>

            <button
              className="w-full bg-[#6CD800] hover:bg-[#7ce600] text-black font-bold py-4 rounded-xl shadow-lg transition-all active:scale-[0.98] mt-4"
              onClick={handleSaveProfile}
            >
              Save Changes
            </button>
          </div>
        </div>
      ) : aboutMode ? (
        <div className="max-w-xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-8">
            <button onClick={() => setAboutMode(false)} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-2xl font-bold text-white">About FitScan</h1>
          </div>
          <div ref={aboutRef} className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-8 text-center shadow-xl">
            <div className="w-20 h-20 bg-[#6CD800] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#6CD800]/20">
              <svg width="40" height="40" viewBox="0 0 448 512"><path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z" fill="black" /></svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">FitScan v1.0.0</h2>
            <p className="text-gray-400 leading-relaxed mb-6">
              Your all-in-one gym and fitness management platform, built to simplify attendance tracking and empower trainers.
            </p>
            <div className="pt-6 border-t border-[#2b2b2b] text-left space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Support</span>
                <a href="mailto:support@fitscan.in" className="text-[#6CD800]">support@fitscan.in</a>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Website</span>
                <span className="text-white">www.fitscan.in</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="section-padding py-8 space-y-8 max-w-xl mx-auto px-4">
          <div className="flex flex-col items-center gap-3 text-white pb-4">
            <div
              className="w-24 h-24 rounded-full bg-[#6CD800] text-[#181f10] flex items-center justify-center text-4xl font-bold overflow-hidden cursor-pointer group relative"
              onClick={() => setEditMode(true)}
            >
              {user.photoUrl ? (
                <img
                  src={user.photoUrl.replace(/\\/g, '/')}
                  alt="Profile"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              ) : (
                <User size={40} className="text-[#181f10]" />
              )}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Edit3 size={20} className="text-white" />
              </div>
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold tracking-tight">{user.name || "Trainer Name"}</h2>
              <p className="text-[#6CD800] text-sm font-medium mt-0.5">Certified Trainer</p>
            </div>
          </div>


          <div className="space-y-3">
            {[
              { icon: <User size={20} />, label: "Edit Your Profile", onClick: handleEditClick },
              { icon: <HelpIcon size={20} />, label: "Help", onClick: () => setHelpMode(true) },
              { icon: <InfoIcon size={20} />, label: "About", onClick: handleAboutClick },
            ].map((item, idx) => (
              <button
                key={idx}
                onClick={item.onClick}
                className="flex items-center justify-between w-full px-5 py-4 bg-[#161616] hover:bg-[#202020] border border-[#2b2b2b] rounded-2xl text-gray-200 transition-all duration-200 active:scale-[0.98] group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#222] flex items-center justify-center text-[#6CD800] group-hover:bg-[#6CD800] group-hover:text-black transition-colors duration-300">
                    {item.icon}
                  </div>
                  <span className="font-medium text-[15px]">{item.label}</span>
                </div>
                <ChevronRight size={18} className="text-gray-600 group-hover:text-[#6CD800] transition-colors" />
              </button>
            ))}
          </div>

          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-red-500 border border-red-500 hover:bg-red-500 hover:text-white transition py-2 rounded-xl font-semibold">
            <LogOut size={18} />
            Log out
          </button>

        </div>
      )}

      {/* CROPPING MODAL */}
      {cropOpen && imageSrc && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1000] p-4">
          <div className="bg-[#222] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto text-white">
            <div className="relative h-96 w-full mb-4 bg-gray-800 rounded-lg">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropDoubleClick={() => setZoom(1)}
                onZoomChange={setZoom}
                onMediaLoaded={() => setCroppedAreaPixels(null)}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setCropOpen(false);
                  setImageSrc(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={getCroppedImage}
                className="px-6 py-2 bg-[#6CD800] hover:bg-[#6CD800]/90 text-[#181f10] rounded-lg font-medium disabled:opacity-50"
                disabled={!croppedAreaPixels}
              >
                Crop & Use
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ProfileSettings;
